import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Download, Upload, FileText, CircleDot, Search, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STATUS_DOT = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-rose-500",
};

const SOURCE_BADGE = {
  scraper: "bg-blue-50 text-blue-700 border-blue-200",
  manual: "bg-purple-50 text-purple-700 border-purple-200",
  manual_legacy: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const CATEGORY_LABELS = {
  set_informativo: "Set Informativo",
  modulo_operativo: "Modulo Operativo",
  pog: "POG",
  tariffa: "Tariffa",
  guida: "Guida Medica",
  altro: "Altro",
};

const CATALOG_OPTIONS = [
  { value: "ris", label: "RealmenteInSalute (RiS)" },
  { value: "realmente_vicini_impresa", label: "Realmente Vicini Impresa (CAT NAT)" },
  { value: "ris_extra", label: "RiS — extra (manuale)" },
  { value: "altro", label: "Altro (manuale)" },
];

export default function RealeMutuaDocsTab() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filterSlug, setFilterSlug] = useState("");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterSlug ? `${API}/admin/realemutua-docs?slug=${filterSlug}` : `${API}/admin/realemutua-docs`;
      const r = await axios.get(url, { withCredentials: true });
      setDocs(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documenti");
    } finally {
      setLoading(false);
    }
  }, [filterSlug]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/realemutua-docs/refresh`, {}, { withCredentials: true });
      const totals = (r.data.results || []).reduce((acc, x) => {
        acc.new += x.new || 0;
        acc.updated += x.updated || 0;
        acc.unchanged += x.unchanged || 0;
        return acc;
      }, { new: 0, updated: 0, unchanged: 0 });
      toast.success(`Refresh completato: ${totals.new} nuovi · ${totals.updated} aggiornati · ${totals.unchanged} invariati`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const refreshOne = async (slug) => {
    try {
      const r = await axios.post(`${API}/admin/realemutua-docs/refresh/${slug}`, {}, { withCredentials: true });
      const x = r.data.result;
      toast.success(`${slug}: ${x.new} nuovi · ${x.updated} aggiornati · ${x.unchanged} invariati`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore refresh");
    }
  };

  const deleteDoc = async (id, fileName) => {
    if (!window.confirm(`Eliminare "${fileName}"? Il file viene archiviato (audit IVASS 10y).`)) return;
    try {
      await axios.delete(`${API}/admin/realemutua-docs/${id}`, { withCredentials: true });
      toast.success("Documento archiviato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const filteredDocs = docs.filter(d => {
    if (search && !d.file_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const slugs = [...new Set(docs.map(d => d.slug))];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-[#163321] flex items-center gap-2">
              <FileText className="w-5 h-5" /> Set Informativo Reale Mutua
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Documenti precontrattuali ai sensi del <strong>Reg. IVASS 41/2018 art. 24</strong>. Refresh automatico ogni notte (04:00) sui prodotti del catalogo automatico (RiS, Realmente Vicini Impresa). Upload manuale per POG, Tariffa, Mod. 5126, Mod. 5076, Guida Medica.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={refreshAll} disabled={refreshing} className="bg-[#163321] hover:bg-[#163321]/90 text-white">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh totale
            </Button>
            <Button onClick={() => setUploadOpen(true)} variant="outline">
              <Upload className="w-4 h-4 mr-2" /> Upload manuale
            </Button>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-zinc-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
        <Select value={filterSlug || "all"} onValueChange={(v) => setFilterSlug(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-56 text-sm"><SelectValue placeholder="Tutti i prodotti" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i prodotti</SelectItem>
            {slugs.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
          <Input placeholder="Cerca per nome file..." className="pl-9 h-9 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Ricarica</Button>
        {slugs.includes("ris") && <Button onClick={() => refreshOne("ris")} variant="outline" size="sm">Refresh RiS</Button>}
        {slugs.includes("realmente_vicini_impresa") && <Button onClick={() => refreshOne("realmente_vicini_impresa")} variant="outline" size="sm">Refresh RVI</Button>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <StatCard color="emerald" label="Verde (≤7 gg)" value={docs.filter(d => d.status_color === "green").length} />
        <StatCard color="amber" label="Giallo (7-14 gg)" value={docs.filter(d => d.status_color === "yellow").length} />
        <StatCard color="rose" label="Rosso (>14 gg)" value={docs.filter(d => d.status_color === "red").length} />
        <StatCard color="zinc" label="Totale" value={docs.length} />
      </div>

      {/* Tabella */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs text-zinc-600 uppercase tracking-wider">
                <th className="px-3 py-2.5 w-6"></th>
                <th className="px-3 py-2.5">Prodotto · File</th>
                <th className="px-3 py-2.5">Categoria</th>
                <th className="px-3 py-2.5">Edizione</th>
                <th className="px-3 py-2.5 hidden md:table-cell">Hash</th>
                <th className="px-3 py-2.5 hidden lg:table-cell">Aggiornato il</th>
                <th className="px-3 py-2.5">Source</th>
                <th className="px-3 py-2.5 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400 text-sm">{loading ? "Caricamento..." : "Nessun documento trovato"}</td></tr>
              )}
              {filteredDocs.map((d) => (
                <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-3 py-3"><CircleDot className={`w-3 h-3 rounded-full ${STATUS_DOT[d.status_color] || "bg-zinc-300"}`} /></td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-zinc-900 text-sm break-all max-w-md">{d.file_name}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{d.label} · {d.slug}</div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                      {CATEGORY_LABELS[d.category] || d.category}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-700">{d.edition || "—"}</td>
                  <td className="px-3 py-3 hidden md:table-cell font-mono text-[10px] text-zinc-500">{d.current_hash}</td>
                  <td className="px-3 py-3 hidden lg:table-cell text-xs text-zinc-600">
                    {d.last_updated ? new Date(d.last_updated).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${SOURCE_BADGE[d.source] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                      {d.source}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <a
                        href={`${API}/realemutua-docs/${d.slug}/${d.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </a>
                      <button
                        onClick={() => deleteDoc(d.id, d.file_name)}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs text-rose-600 hover:bg-rose-50"
                        title="Archivia (soft delete)"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onSuccess={load} />}
    </div>
  );
}


function StatCard({ color, label, value }) {
  const colorMap = {
    emerald: "border-emerald-200 bg-emerald-50/60",
    amber: "border-amber-200 bg-amber-50/60",
    rose: "border-rose-200 bg-rose-50/60",
    zinc: "border-zinc-200 bg-white",
  };
  return (
    <div className={`border rounded-lg p-3 ${colorMap[color]}`}>
      <div className="text-xs text-zinc-600">{label}</div>
      <div className="text-2xl font-heading text-zinc-900 mt-0.5">{value}</div>
    </div>
  );
}


function UploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [slug, setSlug] = useState("ris");
  const [fileName, setFileName] = useState("");
  const [category, setCategory] = useState("modulo_operativo");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !slug || !fileName) {
      toast.error("Compila file, slug, file_name");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slug", slug);
      fd.append("file_name", fileName);
      fd.append("category", category);
      const r = await axios.post(`${API}/admin/realemutua-docs/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Upload OK (${r.data.kind})`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4">
        <h3 className="font-heading text-lg text-[#163321]">Upload manuale documento</h3>
        <p className="text-xs text-zinc-500">Per PDF non disponibili nell'API Reale Mutua: POG, Tariffa, Mod. 5126, Mod. 5076, Guida Medica, Benchmarking.</p>

        <div className="space-y-2">
          <Label className="text-xs">PDF (max 20MB)</Label>
          <Input type="file" accept="application/pdf,.pdf" onChange={(e) => {
            const f = e.target.files?.[0];
            setFile(f);
            if (f && !fileName) setFileName(f.name);
          }} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Prodotto (slug)</Label>
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATALOG_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="set_informativo">Set Informativo</SelectItem>
                <SelectItem value="modulo_operativo">Modulo operativo (5126/5076)</SelectItem>
                <SelectItem value="tariffa">Tariffa</SelectItem>
                <SelectItem value="pog">POG</SelectItem>
                <SelectItem value="guida">Guida medica</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Nome file (es. "ModuloAdesione_5126_Ed.012025.pdf")</Label>
          <Input value={fileName} onChange={(e) => setFileName(e.target.value)} className="text-sm" />
          <p className="text-[10px] text-zinc-400 mt-1">Tip: includi "Ed. YYYY.MM" per il tracking edizioni.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button type="submit" size="sm" disabled={busy} className="bg-[#163321] hover:bg-[#163321]/90 text-white">
            {busy ? "Caricamento..." : "Carica"}
          </Button>
        </div>
      </form>
    </div>
  );
}
